import React, { useState } from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const maskCPF = (v) =>
  v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const maskPhone = (v) =>
  v
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2")
    .replace(/(\d{4})(\d)/, "$1-$2");

export default function LeadForm() {
  const [form, setForm] = useState({ full_name: "", cpf: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (key) => (e) => {
    let value = e.target.value;
    if (key === "cpf") value = maskCPF(value);
    if (key === "phone") value = maskPhone(value);
    setForm((f) => ({ ...f, [key]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || form.cpf.length < 14 || form.phone.length < 14) {
      toast.error("Preencha todos os campos corretamente.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", form);
      setDone(true);
      toast.success("Recebemos seus dados! Em breve entraremos em contato.");
      setForm({ full_name: "", cpf: "", phone: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao enviar. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="cadastro" className="scroll-mt-nav relative bg-efory-ink text-white py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 bg-grid-dark opacity-40" />
      <div className="absolute -bottom-32 -left-24 h-[380px] w-[380px] rounded-full bg-efory-blue/15 blur-[120px]" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-block rounded-full border border-efory-blue/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-efory-blue">
            Fale com a EFORY
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-6 leading-[1.08]">
            Dê o primeiro passo para{" "}
            <span className="text-gradient-blue">crescer com previsibilidade</span>
          </h2>
          <p className="mt-5 text-white/65 text-base sm:text-lg leading-relaxed max-w-md">
            Preencha seus dados e nossa equipe entrará em contato para entender o seu momento e
            apresentar a solução ideal.
          </p>
          <div className="mt-7 flex items-center gap-3 text-sm text-white/60">
            <ShieldCheck className="h-5 w-5 text-efory-blue shrink-0" />
            Suas informações estão seguras e não serão compartilhadas.
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-7 md:p-9"
        >
          {done ? (
            <div data-testid="lead-success" className="flex flex-col items-center text-center py-10">
              <CheckCircle2 className="h-16 w-16 text-efory-blue" />
              <h3 className="font-heading text-2xl font-bold mt-5">Recebemos seu cadastro!</h3>
              <p className="text-white/60 mt-2 max-w-sm">
                Em breve entraremos em contato pelo WhatsApp informado.
              </p>
              <Button
                onClick={() => setDone(false)}
                variant="outline"
                data-testid="lead-new-button"
                className="mt-6 rounded-full border-white/20 bg-transparent text-white hover:bg-white/10"
              >
                Enviar outro cadastro
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
              <div>
                <Label htmlFor="full_name" className="text-white/80 text-sm">Nome completo</Label>
                <Input
                  id="full_name"
                  data-testid="lead-name-input"
                  value={form.full_name}
                  onChange={onChange("full_name")}
                  placeholder="Seu nome completo"
                  className="mt-2 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-efory-blue focus-visible:border-efory-blue"
                />
              </div>
              <div>
                <Label htmlFor="cpf" className="text-white/80 text-sm">CPF</Label>
                <Input
                  id="cpf"
                  data-testid="lead-cpf-input"
                  value={form.cpf}
                  onChange={onChange("cpf")}
                  inputMode="numeric"
                  placeholder="000.000.000-00"
                  className="mt-2 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-efory-blue focus-visible:border-efory-blue"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-white/80 text-sm">Telefone / WhatsApp</Label>
                <Input
                  id="phone"
                  data-testid="lead-phone-input"
                  value={form.phone}
                  onChange={onChange("phone")}
                  inputMode="numeric"
                  placeholder="(31) 99999-9999"
                  className="mt-2 h-12 bg-white/5 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-efory-blue focus-visible:border-efory-blue"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid="lead-submit-button"
                className="w-full h-12 bg-efory-blue hover:bg-efory-bluedark text-white rounded-full font-semibold text-base transition-all duration-300 hover:scale-[1.02]"
              >
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Quero falar com a EFORY"}
              </Button>
              <p className="text-center text-xs text-white/45 flex items-center justify-center gap-1.5">
                <ShieldCheck className="h-3.5 w-3.5" />
                Suas informações estão seguras e não serão compartilhadas.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
